"use client";

import { useEffect, useState, type Dispatch, type SetStateAction } from "react";
import type { AgenticCartBundleSummaryResponse } from "@ishvaram/api-client";
import { getSavedKundali } from "@/lib/api/kundali";
import { getAgenticCartBundle } from "@/lib/api/plans";
import { getServiceability, type ServiceabilityInfo } from "@/lib/api/serviceability";
import { readCartCheckoutRecoveryContext } from "@/lib/cart-checkout-recovery";
import { isCompleteIndianPincode, resolveIndianPincode } from "@/lib/pincode";
import { DigitalCartCheckout, DigitalCartSuccess } from "./AgenticCartCheckout.digital";
import { readCartConfirmation } from "./AgenticCartCheckout.confirmation";
import { useCartConfirmation } from "./AgenticCartCheckout.confirmation-hook";
import { AgenticCartLayout } from "./AgenticCartCheckout.layout";
import { startAgenticCartCheckout } from "./AgenticCartCheckout.payment";
import { AgenticCartSuccess } from "./AgenticCartCheckout.summary";
import { trackNativeBundleShown, trackNativeCartOpened } from "./AgenticCartCheckout.telemetry";
import type {
  CartRecoveryState,
  CartStatus,
  ContactForm,
  AgenticCartCheckoutProps,
  ShippingForm,
  SuccessState,
} from "./AgenticCartCheckout.types";

export default function AgenticCartCheckout({
  bundleId,
  sourcePath,
  intent,
  customerProblem,
  agentTraceId,
  journeyActionId,
  concernId,
  readingId,
  recovered = false,
  language: initialLanguage = "en",
}: AgenticCartCheckoutProps) {
  const [language, setLanguage] = useState<"en" | "hi">(initialLanguage);
  const [status, setStatus] = useState<CartStatus>("loading");
  const [bundle, setBundle] = useState<AgenticCartBundleSummaryResponse | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState<SuccessState | null>(null);
  const [contact, setContact] = useState<ContactForm>(emptyContact());
  const [shipping, setShipping] = useState<ShippingForm>(emptyShipping());
  const [recovery, setRecovery] = useState<CartRecoveryState | null>(null);
  const [resumed, setResumed] = useState(false);
  const [serviceability, setServiceability] = useState<ServiceabilityInfo | null>(null);
  // WhatsApp order/delivery opt-in — pre-checked (utility framing, rule 22).
  // Native cart always collects a phone, so the box always renders; its value
  // threads into recovery_phone_consent on the abandon payload.
  const [whatsappConsent, setWhatsappConsent] = useState(true);
  const recheckConfirmation = useCartConfirmation(bundle, status, {
    onStatus: setStatus,
    onSuccess: setSuccess,
    onError: setError,
  });

  useEffect(() => {
    let active = true;
    getAgenticCartBundle(bundleId)
      .then((data) => {
        if (!active) return;
        setBundle(data);
        const pending = readCartConfirmation(bundleId);
        setStatus(pending ? "verification_pending" : "ready");
        if (pending) setLanguage(pending.language);
        // Returning via a recovery link: pre-fill saved contact details for
        // this bundle so finishing checkout is one tap (mirrors the
        // report-CTA resume). Applied here (post-fetch, client-only) so it
        // never reads localStorage during SSR / hydration.
        restoreCartContact(bundleId, recovered, setContact, setResumed);
        const telemetry = {
          bundle: data,
          sourcePath,
          intent: intent || data.intent,
          customerProblem: customerProblem || data.customer_problem,
          agentTraceId,
          journeyActionId,
          concernId,
        };
        trackNativeBundleShown(telemetry);
        trackNativeCartOpened(telemetry);
      })
      .catch((err) => {
        if (!active) return;
        setError(err instanceof Error ? err.message : "Could not load this upay plan.");
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [
    agentTraceId,
    bundleId,
    concernId,
    customerProblem,
    intent,
    journeyActionId,
    recovered,
    sourcePath,
  ]);

  useReadingPrefill(readingId, setContact);

  function updateContactField(field: keyof ContactForm, value: string) {
    setContact((prev) => ({ ...prev, [field]: value }));
    if (field === "phone") {
      setShipping((prev) => ({ ...prev, phone: prev.phone || value }));
    }
  }

  function updateShippingField(field: keyof ShippingForm, value: string) {
    setShipping((prev) => ({ ...prev, [field]: value }));
    if (field === "zip") applyPincodeAutofill(value, setShipping, setServiceability);
  }

  async function handlePay() {
    if (!bundle || status !== "ready") return;
    setRecovery(null);
    await startAgenticCartCheckout({
      bundle,
      contact,
      shipping,
      sourcePath,
      intent: intent || bundle.intent,
      customerProblem: customerProblem || bundle.customer_problem,
      agentTraceId,
      journeyActionId,
      concernId,
      readingId,
      language,
      whatsappConsent,
      onSuccess: setSuccess,
      onStatus: setStatus,
      onError: setError,
      onRecovery: setRecovery,
    });
  }

  if (success && bundle) {
    return !bundle.requires_shipping && !bundle.sankalp_program ? (
      <DigitalCartSuccess bundle={bundle} success={success} language={language} />
    ) : (
      <AgenticCartSuccess bundle={bundle} success={success} />
    );
  }

  if (bundle && !bundle.requires_shipping && !bundle.sankalp_program) {
    return (
      <DigitalCartCheckout
        bundle={bundle}
        contact={contact}
        status={status}
        error={error}
        language={language}
        onLanguageChange={setLanguage}
        onContactChange={updateContactField}
        onPay={handlePay}
        onCheckConfirmation={recheckConfirmation}
        recovery={recovery}
        whatsappConsent={whatsappConsent}
        onWhatsappConsentChange={setWhatsappConsent}
      />
    );
  }

  return (
    <AgenticCartLayout
      bundle={bundle}
      contact={contact}
      shipping={shipping}
      serviceability={serviceability}
      status={status}
      visibleError={error}
      canPay={status === "ready" && Boolean(bundle)}
      onContactChange={updateContactField}
      onShippingChange={updateShippingField}
      onPay={handlePay}
      recovery={recovery}
      resumed={resumed}
      whatsappConsent={whatsappConsent}
      onWhatsappConsentChange={setWhatsappConsent}
      birthPrefilled={Boolean(readingId && contact.name && contact.birthDate && contact.birthPlace)}
    />
  );
}

function useReadingPrefill(
  readingId: string | undefined,
  setContact: Dispatch<SetStateAction<ContactForm>>
) {
  // Prefill the report's birth details from the reading this offer came from,
  // so a seeker who already entered name/place/date/time in chat does not retype
  // them. Additive + soft-failing: no readingId or a failed fetch leaves the form
  // empty (the prior behavior). Only fills EMPTY fields, never overwriting input.
  useEffect(() => {
    if (!readingId) return;
    let active = true;
    getSavedKundali(readingId)
      .then((reading) => {
        if (!active) return;
        const b = reading.birth_details;
        if (!b) return;
        setContact((prev) => ({
          ...prev,
          name: prev.name || b.name || "",
          birthDate: prev.birthDate || b.birth_date || "",
          birthTime: prev.birthTime || (b.birth_time ? b.birth_time.slice(0, 5) : ""),
          birthPlace: prev.birthPlace || b.birth_place || "",
        }));
      })
      .catch(() => {
        // Soft-fail: form stays empty and the seeker types it, exactly as before.
      });
    return () => {
      active = false;
    };
  }, [readingId, setContact]);
}

// Pincode-autofill: a complete 6-digit PIN fills city + state (India Post API),
// cutting the form to pincode + house/street. Only fills EMPTY fields so a manual
// correction is never overwritten; soft-fails silently. Also fetches Shiprocket
// deliverability / COD / ETA. Module-level so the component stays within the
// max-lines lint.
function applyPincodeAutofill(
  value: string,
  setShipping: Dispatch<SetStateAction<ShippingForm>>,
  setServiceability: Dispatch<SetStateAction<ServiceabilityInfo | null>>
) {
  if (!isCompleteIndianPincode(value)) {
    setServiceability(null);
    return;
  }
  void resolveIndianPincode(value).then((loc) => {
    if (!loc) return;
    setShipping((prev) => ({
      ...prev,
      city: prev.city || loc.city,
      province: prev.province || loc.state,
    }));
  });
  void getServiceability(value).then(setServiceability);
}

function emptyContact(): ContactForm {
  return {
    name: "",
    birthDate: "",
    birthTime: "",
    birthPlace: "",
    email: "",
    phone: "",
  };
}

function emptyShipping(): ShippingForm {
  return {
    first_name: "",
    last_name: "",
    address1: "",
    address2: "",
    city: "",
    province: "",
    country_code: "IN",
    zip: "",
    phone: "",
  };
}

function restoreCartContact(
  bundleId: string,
  recovered: boolean,
  setContact: Dispatch<SetStateAction<ContactForm>>,
  setResumed: Dispatch<SetStateAction<boolean>>
) {
  if (recovered) {
    const saved = readCartCheckoutRecoveryContext();
    if (saved && saved.bundleId === bundleId) {
      setContact((prev) => ({
        ...prev,
        email: prev.email || saved.customerEmail || "",
        phone: prev.phone || saved.customerPhone || "",
      }));
      setResumed(true);
    }
  }
}
